import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useQueries } from "react-query";
import {
  transformArrayField,
  transformTextField,
  generateTextFieldLookup,
  generateArrayFieldLookup,
} from "../../../utils/dataTransforms";
import { useTableFilterLookups } from "../../../hooks/useTableFilterLookups";
import MaterialTable from "material-table";
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Button,
  Box,
  Link,
  Divider as MuiDivider,
  Breadcrumbs as MuiBreadcrumbs,
} from "@material-ui/core";
import { useAuth0 } from "@auth0/auth0-react";
import PlusIcon from "@material-ui/icons/Add";
import MinusIcon from "@material-ui/icons/Remove";
import { Alert } from "@material-ui/lab";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import { spacing } from "@material-ui/system";
import styled from "styled-components/macro";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const BASE_URL = process.env.REACT_APP_ENDPOINT;
const QUERY_CONFIGS = [
  {
    queryKey: "ui-cq-pumpage-calendar-crosstab-bypermit",
    url: `${BASE_URL}/api/ui-cq-pumpage-calendar-crosstab-bypermit`,
  },
  {
    queryKey: "ui-cq-pumpage-fiscal-crosstab-bypermit",
    url: `${BASE_URL}/api/ui-cq-pumpage-fiscal-crosstab-bypermit`,
  },
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const FISCAL_NAMES = [
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
];

const tableOptions = {
  emptyRowsWhenPaging: false,
  exportAllData: true,
  columnsButton: true,
  exportButton: { csv: true },
  pageSize: 30,
  pageSizeOptions: [5, 10, 30, 60],
  padding: "dense",
  searchFieldAlignment: "left",
  showTitle: false,
  maxBodyHeight: "600px",
  filtering: true,
  headerStyle: {
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  cellStyle: {
    whiteSpace: "nowrap",
  },
};

function useDebounce(callback, delay) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    (...args) => {
      if (callbackRef.current.timeoutId) {
        clearTimeout(callbackRef.current.timeoutId);
      }
      callbackRef.current.timeoutId = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
}

function getOrderedMonths(yearOption) {
  return yearOption === "calendar" ? MONTH_NAMES : FISCAL_NAMES;
}

function formatNumber(value) {
  return value != null ? Number(value).toLocaleString() : "";
}

const MultiSelect = ({ label, options, selected, onChange, getLabel }) => {
  const isAllSelected =
    options.length > 0 && selected.length === options.length;

  const handleToggleAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const handleChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === "selectAll") {
      handleToggleAll();
    } else {
      onChange(value);
    }
  };

  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        multiple
        value={selected}
        onChange={handleChange}
        renderValue={(selected) => selected.map(getLabel).join(", ")}
      >
        {options.length > 0 && (
          <MenuItem
            value="selectAll"
            onClick={handleToggleAll}
            style={{
              fontWeight: 600,
              padding: "16px",
              justifyContent: "center",
              color: "#17A5B9",
              gap: "6px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                border: "2px solid #17A5B9",
                color: "#17A5B9",
              }}
            >
              {isAllSelected ? <MinusIcon /> : <PlusIcon />}
            </span>
            {isAllSelected ? "Deselect All" : "Select All"}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={getLabel(option)} value={option}>
            <Checkbox
              checked={selected.some(
                (item) => getLabel(item) === getLabel(option)
              )}
            />
            <ListItemText primary={getLabel(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default function PumpageByPermit() {
  const { getAccessTokenSilently } = useAuth0();

  const results = useQueries(
    QUERY_CONFIGS.map((config) => ({
      queryKey: config.queryKey,
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(config.url, { headers });
        return data;
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    }))
  );

  // Extracting data, loading, and error states from results
  const [{ data: calendarData = [] }, { data: fiscalData = [] }] = results;

  const hasError = results.some((result) => result.error);
  const isLoading = results.some((result) => result.isLoading);

  const [filters, setFilters] = useState({
    reportYearOption: "calendar",
    startYear: 2008,
    endYear: new Date().getFullYear(),

    aquiferGroupList: [],
    mgmtZoneList: [],
    permitTypeList: [],
    permitUseList: [],
    permitStatusList: [],

    permitteeInfo: "",
  });

  // debounce permittee info
  const [permitteeDraft, setPermitteeDraft] = useState("");

  // only set default filters once
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Combine the two datasets and build lookups
  const combinedData = useMemo(() => {
    return [...calendarData, ...fiscalData];
  }, [calendarData, fiscalData]);

  const lookupValues = useMemo(() => {
    return {
      aquiferGroupList: generateTextFieldLookup(combinedData, "aquifer_group"),
      mgmtZoneList: generateArrayFieldLookup(combinedData, "management_zones"),
      permitTypeList: generateTextFieldLookup(combinedData, "permit_type"),
      permitUseList: generateTextFieldLookup(combinedData, "permit_use"),
      permitStatusList: generateTextFieldLookup(combinedData, "permit_status"),
    };
  }, [combinedData]);

  // Initialize filters with "all" possible values only once
  useEffect(() => {
    if (
      !filtersInitialized &&
      lookupValues.aquiferGroupList.length > 0 &&
      combinedData.length
    ) {
      setFilters((prev) => ({
        ...prev,
        aquiferGroupList: lookupValues.aquiferGroupList,
        mgmtZoneList: lookupValues.mgmtZoneList,
        permitTypeList: lookupValues.permitTypeList,
        permitUseList: lookupValues.permitUseList,
        permitStatusList: lookupValues.permitStatusList,
      }));
      setFiltersInitialized(true);
    }
  }, [filtersInitialized, lookupValues, combinedData]);

  const debouncedSetPermitteeInfo = useDebounce((value) => {
    setFilters((prev) => ({ ...prev, permitteeInfo: value }));
  }, 200);

  const handlePermitteeChange = useCallback(
    (e) => {
      const { value } = e.target;
      setPermitteeDraft(value);
      debouncedSetPermitteeInfo(value);
    },
    [debouncedSetPermitteeInfo]
  );

  // Filter the data based on the selected filters
  const displayedData = useMemo(() => {
    const rawData =
      filters.reportYearOption === "calendar" ? calendarData : fiscalData;
    if (!rawData.length) return [];

    const {
      startYear,
      endYear,
      aquiferGroupList,
      mgmtZoneList,
      permitTypeList,
      permitUseList,
      permitStatusList,
      permitteeInfo,
    } = filters;

    const transformedData = rawData.map((row) => ({
      ...row,
      // Transform fields for display and searchability
      aquifer_group: transformTextField(row.aquifer_group),
      management_zones: transformArrayField(row.management_zones),
      permit_type: transformTextField(row.permit_type),
      permit_use: transformTextField(row.permit_use),
      permit_status: transformTextField(row.permit_status),
      permittee_info: transformArrayField(row.permittee_info),
    }));

    return transformedData.filter((row) => {
      const year =
        filters.reportYearOption === "calendar" ? row.c_year : row.fiscal_year;
      if (typeof year !== "number" || year < startYear || year > endYear) {
        return false;
      }

      const checkNull = (value, list) => {
        if (value === null) return list.includes("<blank>");
        return list.includes(value);
      };

      const checkArrayField = (value, list) => {
        if (value === null || value === undefined || value === "<empty>") {
          return list.includes("<empty>");
        }
        if (Array.isArray(value)) {
          if (value.length === 0 || (value.length === 1 && value[0] === null)) {
            return list.includes("<empty>");
          }
          return list.some((listItem) => {
            if (listItem === "<empty>") return false;
            return value.includes(listItem);
          });
        }
        // Handle comma-separated strings from transformArrayField
        if (typeof value === "string" && value.includes(", ")) {
          const parts = value.split(", ");
          return parts.some((part) => list.includes(part));
        }
        return list.includes(value);
      };

      if (!checkNull(row.aquifer_group, aquiferGroupList)) return false;
      if (!checkArrayField(row.management_zones, mgmtZoneList)) return false;
      if (!checkNull(row.permit_type, permitTypeList)) return false;
      if (!checkNull(row.permit_use, permitUseList)) return false;
      if (!checkNull(row.permit_status, permitStatusList)) return false;

      if (permitteeInfo) {
        if (
          !row.permittee_info
            .toLowerCase()
            .includes(permitteeInfo.toLowerCase())
        ) {
          return false;
        }
      }

      return true;
    });
  }, [filters, calendarData, fiscalData]);

  // Generate table filter lookups from the transformed data
  // Only for fields that have select/dropdown filters in the UI
  const tableFilterLookups = useTableFilterLookups(displayedData, {
    aquifer_group: { type: "text", field: "aquifer_group" },
    management_zones: { type: "array", field: "management_zones" },
    permit_type: { type: "text", field: "permit_type" },
    permit_use: { type: "text", field: "permit_use" },
    permit_status: { type: "text", field: "permit_status" },
  });

  const columns = useMemo(() => {
    const yearCol = {
      title:
        filters.reportYearOption === "calendar"
          ? "Calendar Year"
          : "Fiscal Year",
      field: filters.reportYearOption === "calendar" ? "c_year" : "fiscal_year",
      cellStyle: { whiteSpace: "nowrap" },
    };

    const orderedMonths = getOrderedMonths(filters.reportYearOption);

    const monthCols = orderedMonths.map((month) => ({
      title: month,
      field: `${month.toLowerCase().substring(0, 3)}_gallons`,
      render: (row) =>
        formatNumber(row[`${month.toLowerCase().substring(0, 3)}_gallons`]),
      cellStyle: { whiteSpace: "nowrap" },
      filtering: false,
    }));

    const additionalCols = [
      {
        title: "Total",
        field: "tot_gallons",
        render: (row) => formatNumber(row.tot_gallons),
        cellStyle: { whiteSpace: "nowrap" },
        filtering: false,
      },
      {
        title: "Aquifer Group",
        field: "aquifer_group",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.aquifer_group,
      },
      {
        title: "Management Zone",
        field: "management_zones",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.management_zones,
      },
      {
        title: "Permit Type",
        field: "permit_type",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_type,
      },
      {
        title: "Permit Use",
        field: "permit_use",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_use,
      },
      {
        title: "Permit Status",
        field: "permit_status",
        cellStyle: { whiteSpace: "nowrap" },
        lookup: tableFilterLookups.permit_status,
      },
      {
        title: "Permittee Info",
        field: "permittee_info",
        cellStyle: { whiteSpace: "nowrap" },
      },
    ];

    return [
      {
        title: "Permit Number",
        field: "permit_number",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        title: "Permit Name",
        field: "permit_name",
        cellStyle: { whiteSpace: "nowrap" },
      },
      yearCol,
      ...monthCols,
      ...additionalCols,
    ];
  }, [filters, tableFilterLookups]);

  const updateFilter = useCallback((field, value) => {
    setFilters((prev) => {
      return { ...prev, [field]: value };
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      reportYearOption: "calendar",
      startYear: 2008,
      endYear: new Date().getFullYear(),
      aquiferGroupList: lookupValues.aquiferGroupList,
      mgmtZoneList: lookupValues.mgmtZoneList,
      permitTypeList: lookupValues.permitTypeList,
      permitUseList: lookupValues.permitUseList,
      permitStatusList: lookupValues.permitStatusList,
      permitteeInfo: "",
    });
    setPermitteeDraft("");
  }, [lookupValues]);

  if (hasError) return "An error has occurred. Please try again.";
  return (
    <>
      <Helmet title="Pumpage by Permit" />
      <Typography variant="h3" gutterBottom display="inline">
        Pumpage by Permit
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Pumpage by Permit</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Paper style={{ padding: "10px", marginBottom: "20px" }}>
        <Grid container spacing={3}>
          {/* Report Year Option */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Report Year Option</InputLabel>
              <Select
                label="Report Year Option"
                value={filters.reportYearOption}
                onChange={(e) =>
                  updateFilter("reportYearOption", e.target.value)
                }
              >
                <MenuItem value="calendar">Calendar Year</MenuItem>
                <MenuItem value="fiscal">Fiscal Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Year range */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="From Year"
              type="number"
              variant="outlined"
              fullWidth
              value={filters.startYear}
              onChange={(e) =>
                updateFilter("startYear", Number(e.target.value))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="To Year"
              type="number"
              variant="outlined"
              fullWidth
              value={filters.endYear}
              onChange={(e) => updateFilter("endYear", Number(e.target.value))}
            />
          </Grid>

          {/* Aquifer Group multi-select */}
          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Aquifer Group"
              options={lookupValues.aquiferGroupList}
              selected={filters.aquiferGroupList}
              onChange={(value) => updateFilter("aquiferGroupList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          {/* Management Zone multi-select */}
          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Management Zone"
              options={lookupValues.mgmtZoneList}
              selected={filters.mgmtZoneList}
              onChange={(value) => updateFilter("mgmtZoneList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          {/* Permit Type multi-select */}
          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Type"
              options={lookupValues.permitTypeList}
              selected={filters.permitTypeList}
              onChange={(value) => updateFilter("permitTypeList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          {/* Permit Use multi-select */}
          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Use"
              options={lookupValues.permitUseList}
              selected={filters.permitUseList}
              onChange={(value) => updateFilter("permitUseList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          {/* Permit Status multi-select */}
          <Grid item xs={12} sm={6} md={4}>
            <MultiSelect
              label="Permit Status"
              options={lookupValues.permitStatusList}
              selected={filters.permitStatusList}
              onChange={(value) => updateFilter("permitStatusList", value)}
              getLabel={(item) => item}
            />
          </Grid>

          {/* Permittee (debounced) */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              variant="outlined"
              label="Permittee"
              fullWidth
              value={permitteeDraft}
              onChange={handlePermitteeChange}
            />
          </Grid>
        </Grid>

        <Box my={2} textAlign="right">
          <Button variant="contained" onClick={handleResetFilters}>
            Reset Filters
          </Button>
        </Box>
      </Paper>
      <Paper style={{ padding: "10px" }}>
        {!displayedData?.length > 0 ? (
          <Alert severity="info">
            <Typography
              variant="h6"
              component="div"
              style={{ padding: "16px" }}
            >
              No records available for these search parameters. Please try
              again.
            </Typography>
          </Alert>
        ) : (
          <MaterialTable
            id="Pumpage by Permit Report"
            title="Pumpage by Permit Report"
            columns={columns}
            isLoading={isLoading}
            data={displayedData}
            options={tableOptions}
          />
        )}
      </Paper>
    </>
  );
}
